/**
 * The SPA's provider stack — the port of `src/components/Providers.tsx`.
 *
 * One substitution: next-auth's `<SessionProvider>` becomes `<AuthProvider>`
 * (FE-04). The TanStack Query client is copied with the same defaults, so
 * every `useQuery` moved over from `src/` caches and retries exactly as it
 * does today.
 *
 * `<Toaster />` is added because `sonner` was mounted by a Next.js layout the
 * SPA does not have; several copied components (`EditLayout`,
 * `CashPositionPopup`) call `toast()` and would otherwise fail silently.
 *
 * `<PrivacyProvider>` and `<ThemeProvider>` (V5) reconcile the two
 * module-level preference stores with the server copy in
 * `rent.user_preference`. They render no chrome of their own.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth/AuthProvider";
import { PrivacyProvider } from "@/lib/privacy/PrivacyProvider";
import { ThemeProvider } from "@/lib/theme";

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 30_000, retry: 1 },
      },
    });

    // S3 ("quick data handy"): reference data barely changes within a
    // session — nobody renames a category or adds a bank account mid
    // tab-switch — so these three key prefixes get a 5-minute `staleTime`
    // instead of the 30s app default. That means switching tabs (Income ->
    // Expenses -> Records, all of which query one of these) repaints
    // instantly from cache instead of re-fetching every time. Per-query
    // `queryKey`s vary in their second element (e.g. `["accounts", false]`,
    // `["accounts", id]`) — `setQueryDefaults` matches by PREFIX, so every
    // variant under a key is covered without touching the pages that call
    // `useQuery`. Deliberately NOT touching the global default above: pages
    // this agent doesn't own may rely on the 30s behaviour for their own
    // (non-reference) queries.
    for (const key of ["categories", "accounts", "labels"]) {
      client.setQueryDefaults([key], { staleTime: 5 * 60_000 });
    }

    return client;
  });

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        {/* Both providers sit INSIDE AuthProvider because both are per-user:
            they load the signed-in user's stored preferences and clear them on
            sign-out. Neither renders chrome — they only reconcile a
            module-level store with the server, so their position in the tree
            costs nothing. */}
        <PrivacyProvider>
          <ThemeProvider>
            {children}
            <Toaster position="top-right" richColors closeButton />
          </ThemeProvider>
        </PrivacyProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
